import React, { useMemo, useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { Alert, Button, Form, Input, Space, Typography } from 'antd';
import { isAxiosError } from 'axios';
import type {
  CreateWebhookIntegrationLimitExceededErrorDto,
  SendTestWebhookEventErrorDto
} from '@api/Api';
import { apiClient } from '@api/client';
import CopyableInput from '@components/CopyableInput/CopyableInput';
import type { IntegrationInstallInstructionsProps } from '@pages/AdminSettings/components/IntegrationTab/constants';
import {
  ALL_EVENT_VALUES,
  EventTypesInput,
  HeadersInput,
  SignatureVerificationGuide,
  WebhookHeader,
  WebhookEventType,
  normalizeWebhookUrl,
  validateWebhookHeaders,
  webhookHeadersToPayload,
  validateWebhookUrl
} from '@pages/AdminSettings/components/IntegrationTab/webhook-shared';

const { Title, Paragraph } = Typography;

interface CreateWebhookForm {
  url: string;
  eventTypes: WebhookEventType[];
  headers: WebhookHeader[];
}

interface CreatedWebhookState {
  integrationId: string;
  secret: string;
  url: string;
  headers: WebhookHeader[];
}

export default function WebhookInstallInstructions({
  organizationId,
  onClose
}: IntegrationInstallInstructionsProps) {
  const [form] = Form.useForm<CreateWebhookForm>();
  const [createdWebhook, setCreatedWebhook] =
    useState<CreatedWebhookState | null>(null);
  const [createWebhookErrorDescription, setCreateWebhookErrorDescription] =
    useState<string>('');

  const handleUrlPaste = (event: React.ClipboardEvent<HTMLInputElement>) => {
    const pastedText = event.clipboardData.getData('text');
    const normalized = normalizeWebhookUrl(pastedText);

    if (normalized !== pastedText) {
      event.preventDefault();
      form.setFieldValue('url', normalized);
    }
  };

  const normalizeUrlInForm = () => {
    const currentValue = form.getFieldValue('url') as string | undefined;
    if (!currentValue) {
      return;
    }

    form.setFieldValue('url', normalizeWebhookUrl(currentValue));
  };

  const createWebhook = useMutation({
    mutationFn: async (values: CreateWebhookForm) => {
      const { data } =
        await apiClient.webhook.webhookControllerCreateIntegration({
          organizationId,
          url: values.url,
          eventTypes: ALL_EVENT_VALUES.every((eventType) =>
            values.eventTypes.includes(eventType)
          )
            ? null
            : values.eventTypes,
          headers: webhookHeadersToPayload(values.headers)
        });
      return {
        integrationId: data.integrationId,
        secret: data.secret,
        url: values.url,
        eventTypes: values.eventTypes,
        headers: values.headers
      };
    },
    onError: (error) => {
      if (
        isAxiosError(error) &&
        error.response?.data?.errorCode === 'webhook_integration_limit_exceeded'
      ) {
        const payload = error.response
          .data as CreateWebhookIntegrationLimitExceededErrorDto;
        const { limit } = payload;
        setCreateWebhookErrorDescription(
          `You have reached the maximum number of webhook integrations (${limit}).`
        );
        return;
      }
      setCreateWebhookErrorDescription(
        'Oops! Something went wrong while creating the webhooks integration.'
      );
    },
    onSuccess: (data) => {
      setCreateWebhookErrorDescription('');
      setCreatedWebhook(data);
    }
  });

  const sendTestEvent = useMutation({
    mutationFn: async () => {
      if (!createdWebhook) return;
      await apiClient.webhook.webhookControllerSendTestEvent({
        integrationId: createdWebhook.integrationId,
        url: createdWebhook.url,
        headers: webhookHeadersToPayload(createdWebhook.headers)
      });
    }
  });

  const testEventError = useMemo(() => {
    const err = sendTestEvent.error;
    if (!err) return null;
    if (isAxiosError(err)) {
      const payload = err.response?.data;
      if (payload?.errorCode === 'send_test_event_error') {
        const data = payload as SendTestWebhookEventErrorDto;
        return `Webhook endpoint responded with status ${data.statusCode}${
          data.statusText ? `(${data.statusText})` : ''
        }`;
      }
      return `Unknown error occurred`;
    }
  }, [sendTestEvent.error]);

  if (createdWebhook) {
    return (
      <div
        style={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          minHeight: 0
        }}
      >
        <div style={{ paddingBottom: 12, borderBottom: '1px solid #f0f0f0' }}>
          <Title level={3} style={{ marginBottom: 0 }}>
            Webhooks integration created
          </Title>
        </div>
        <div style={{ flex: 1, overflowY: 'auto', paddingRight: 4 }}>
          <Space.Compact
            style={{ width: '100%', paddingTop: 16, paddingBottom: 16 }}
          >
            <Input size="small" readOnly value={createdWebhook.url} />
            <Button
              type="dashed"
              size="small"
              onClick={() => sendTestEvent.mutate()}
              loading={sendTestEvent.isPending}
            >
              Test
            </Button>
          </Space.Compact>

          {sendTestEvent.isSuccess ? (
            <Alert
              type="success"
              showIcon
              style={{ marginBottom: 16 }}
              message="Test webhook event sent successfully."
            />
          ) : sendTestEvent.isIdle ? (
            <Alert
              type="success"
              showIcon
              style={{ marginBottom: 16 }}
              message="Your webhooks endpoint is connected"
            />
          ) : null}

          {testEventError ? (
            <Alert
              type="error"
              showIcon
              style={{ marginBottom: 16 }}
              message="Failed to send test webhook event"
              description={testEventError}
            />
          ) : null}

          <Title level={4}>
            Strongly recommended: validate event signatures
          </Title>
          <Paragraph style={{ marginBottom: 12 }}>
            Signature validation lets your endpoint verify that each webhook
            request was sent by StreamWork and not forged by a third party.
          </Paragraph>
          <Paragraph style={{ marginBottom: 8 }}>Signing secret</Paragraph>
          <CopyableInput
            readOnly
            size="small"
            value={createdWebhook.secret}
            style={{ marginBottom: 12 }}
          />

          <Alert
            type="warning"
            showIcon
            style={{ marginBottom: 16 }}
            message="Save this secret now"
            description="For security reasons this signing secret will not be shown again. Store it in your password manager or secrets vault."
          />
          <SignatureVerificationGuide />
        </div>
        <div
          style={{
            display: 'flex',
            justifyContent: 'flex-end',
            gap: 8,
            paddingTop: 12,
            borderTop: '1px solid #f0f0f0'
          }}
        >
          <Button type="primary" onClick={onClose}>
            Done
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div
      style={{
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        minHeight: 0
      }}
    >
      <div style={{ paddingBottom: 12, borderBottom: '1px solid #f0f0f0' }}>
        <Title level={3} style={{ marginBottom: 0 }}>
          Receive StreamWork webhooks in your endpoint
        </Title>
      </div>
      <div style={{ flex: 1, overflowY: 'auto', paddingRight: 4 }}>
        <Paragraph style={{ marginTop: 12 }}>
          Add your endpoint URL and choose which StreamWork events should
          trigger webhook calls.
        </Paragraph>

        {createWebhook.isError && (
          <Alert
            type="error"
            showIcon
            style={{ marginBottom: 16 }}
            message="Failed to create webhooks integration"
            description={createWebhookErrorDescription}
          />
        )}

        <Form<CreateWebhookForm>
          form={form}
          layout="vertical"
          initialValues={{
            url: '',
            eventTypes: ALL_EVENT_VALUES,
            headers: []
          }}
          onFinish={(values) => createWebhook.mutate(values)}
        >
          <Form.Item
            name="url"
            label="Webhooks URL"
            rules={[
              { required: true, message: 'Webhooks URL is required' },
              {
                validator: (_, value: string | undefined) =>
                  validateWebhookUrl(value)
              }
            ]}
            extra="Use a public HTTPS endpoint from your backend, Zapier, Make.com, or n8n."
          >
            <Input
              placeholder="https://example.com/webhooks/streamwork"
              onPaste={handleUrlPaste}
              onBlur={normalizeUrlInForm}
            />
          </Form.Item>

          <Form.Item
            name="headers"
            label="Custom headers"
            validateTrigger={['onBlur', 'onSubmit']}
            rules={[
              {
                validator: (_, value: WebhookHeader[] | undefined) =>
                  validateWebhookHeaders(value)
              }
            ]}
            extra="Optional headers added to each webhook request."
          >
            <HeadersInput />
          </Form.Item>

          <Form.Item
            name="eventTypes"
            rules={[
              {
                required: true,
                type: 'array',
                min: 1,
                message: 'Select at least one event'
              }
            ]}
          >
            <EventTypesInput />
          </Form.Item>
        </Form>
      </div>

      <div
        style={{
          display: 'flex',
          justifyContent: 'flex-end',
          gap: 8,
          paddingTop: 12,
          borderTop: '1px solid #f0f0f0'
        }}
      >
        <Button onClick={onClose}>Cancel</Button>
        <Button
          type="primary"
          loading={createWebhook.isLoading}
          onClick={() => form.submit()}
        >
          Create webhooks integration
        </Button>
      </div>
    </div>
  );
}
